import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { fmtError } from "@/lib/api";
import { Loader2, ArrowLeft, ShieldCheck, Lock } from "lucide-react";
import { toast } from "sonner";

const ADMIN_ROLES = ["platform_admin", "community_admin"];

export default function AdminLogin() {
  const { user, adminLogin, adminVerify2fa } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState("credentials");
  const [form, setForm] = useState({ email: "", password: "" });
  const [challengeId, setChallengeId] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const redirected = useRef(false);

  useEffect(() => {
    if (!redirected.current && user && ADMIN_ROLES.includes(user.role)) {
      redirected.current = true;
      navigate("/admin", { replace: true });
    }
  }, [user, navigate]);

  const submitCreds = async (e) => {
    e.preventDefault();
    setError(""); setBusy(true);
    try {
      const data = await adminLogin(form.email, form.password);
      setChallengeId(data.challenge_id);
      setStep("twofa");
      if (data.dev_code) toast.info(`Admin 2FA code: ${data.dev_code}`, { duration: 10000 });
      else toast.success("A verification code has been sent to your admin email");
    } catch (err) { setError(fmtError(err.response?.data?.detail) || err.message); }
    finally { setBusy(false); }
  };

  const submit2fa = async (e) => {
    e.preventDefault();
    setError(""); setBusy(true);
    try {
      await adminVerify2fa(challengeId, code);
      navigate("/admin", { replace: true });
    } catch (err) { setError(fmtError(err.response?.data?.detail) || err.message); }
    finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-[#0B1120] flex flex-col" data-testid="admin-login-screen">
      <div className="max-w-md w-full mx-auto px-6 py-10 flex-1 flex flex-col justify-center">
        <div className="flex items-center gap-2 mb-8">
          <div className="w-11 h-11 rounded-2xl bg-[#F97316] flex items-center justify-center shadow-md">
            <Lock className="w-6 h-6 text-white" />
          </div>
          <span className="text-xl font-bold font-head text-white">HumNest <span className="text-[#F97316]">Admin</span></span>
        </div>
        <h1 className="text-3xl font-bold font-head text-white tracking-tight">Secure admin portal</h1>
        <p className="text-white/50 mt-2 mb-8 text-base flex items-center gap-1.5"><ShieldCheck className="w-4 h-4" /> Restricted access · two-factor protected</p>

        {step === "credentials" && (
          <form onSubmit={submitCreds} className="ll-fade-up" data-testid="admin-creds-form">
            <label className="block mb-4">
              <span className="text-sm font-bold uppercase tracking-wider text-white/60">Admin email</span>
              <input data-testid="admin-email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="admin@localloop.ai" className="mt-1.5 bg-white/5 border-2 border-white/15 text-white rounded-xl p-4 text-lg focus:border-[#F97316] focus:ring-2 focus:ring-[#F97316]/20 transition-colors outline-none w-full placeholder:text-white/30" />
            </label>
            <label className="block mb-4">
              <span className="text-sm font-bold uppercase tracking-wider text-white/60">Password</span>
              <input data-testid="admin-password" type="password" required value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="••••••••" className="mt-1.5 bg-white/5 border-2 border-white/15 text-white rounded-xl p-4 text-lg focus:border-[#F97316] focus:ring-2 focus:ring-[#F97316]/20 transition-colors outline-none w-full placeholder:text-white/30" />
            </label>
            {error && <p data-testid="admin-error" className="text-[#FCA5A5] text-sm font-medium mb-4 bg-[#DC2626]/15 rounded-xl p-3">{error}</p>}
            <button type="submit" disabled={busy} data-testid="admin-login-submit" className="w-full bg-[#F97316] text-white rounded-2xl py-4 px-6 font-bold text-lg shadow-sm hover:-translate-y-0.5 transition-transform active:scale-95 min-h-[56px] flex items-center justify-center disabled:opacity-70">
              {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : "Continue"}
            </button>
          </form>
        )}

        {step === "twofa" && (
          <form onSubmit={submit2fa} className="ll-fade-up" data-testid="admin-2fa-form">
            <button type="button" onClick={() => { setStep("credentials"); setCode(""); setError(""); }} data-testid="admin-2fa-back" className="flex items-center gap-1.5 text-sm font-semibold text-white/60 mb-3"><ArrowLeft className="w-4 h-4" /> Back</button>
            <p className="text-sm text-white/70 mb-4">Enter the 6-digit verification code we sent to your admin email.</p>
            <label className="block mb-4">
              <span className="text-sm font-bold uppercase tracking-wider text-white/60">2FA code</span>
              <input data-testid="admin-2fa-code" type="tel" inputMode="numeric" maxLength={6} required value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} placeholder="••••••" className="mt-1.5 bg-white/5 border-2 border-white/15 text-white rounded-xl p-4 text-lg tracking-[0.4em] text-center focus:border-[#F97316] focus:ring-2 focus:ring-[#F97316]/20 transition-colors outline-none w-full placeholder:text-white/30" />
            </label>
            {error && <p data-testid="admin-error" className="text-[#FCA5A5] text-sm font-medium mb-4 bg-[#DC2626]/15 rounded-xl p-3">{error}</p>}
            <button type="submit" disabled={busy || code.length < 6} data-testid="admin-2fa-submit" className="w-full bg-[#F97316] text-white rounded-2xl py-4 px-6 font-bold text-lg shadow-sm hover:-translate-y-0.5 transition-transform active:scale-95 min-h-[56px] flex items-center justify-center disabled:opacity-60">
              {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : "Verify & enter"}
            </button>
          </form>
        )}

        <button type="button" onClick={() => navigate("/")} className="text-center mt-8 text-white/40 text-sm font-semibold hover:text-white/60">Return to HumNest</button>
      </div>
    </div>
  );
}
